import React from "react";

export default class ShowSequences extends React.Component {
    constructor(props) {
        super(props);

        this.libintensity = this.props.language == "fr"?"Intensité":"Intensity";
        this.libconfidence = this.props.language == "fr"?"Confiance":"Confidence";
        this.libsentiment = this.props.language == "fr"?"Sentiment":"Feeling";


        this.styles = {
            container: {
                width: "100%",
                height: "100%",
                background: "#000000",
                color: "#e2e8f0",
                fontFamily: "Arial, sans-serif",
                fontSize: 12,
                overflow: "hidden",
                boxSizing: "border-box",
                border: "1px solid #292938",
                borderRadius: 7,
                display: "flex",
                flexDirection: "column"
            },
            list: {
                flex: 1,
                minHeight: 0,
                overflowY: "auto",
                overflowX: "hidden",
                padding: 7,
                scrollbarWidth: "none",
                msOverflowStyle: "none"
            },
            sequence: {
                background: "#111116",
                border: "1px solid #292938",
                borderRadius: 6,
                padding: 9,
                marginBottom: 8
            },
            heading: {
                display: "flex",
                alignItems: "center",
                gap: 7,
                marginBottom: 7,
                flexWrap: "wrap"
            },
            number: {
                color: "#ffffff",
                background: "#6554c0",
                borderRadius: 4,
                padding: "3px 7px",
                fontWeight: "bold",
                flexShrink: 0
            },
            timecode: {
                color: "#8f9cff",
                fontSize: 11,
                fontVariantNumeric: "tabular-nums"
            },
            title: {
                color: "#ffffff",
                fontWeight: "bold",
                fontSize: 13,
                marginBottom: 8,
                overflowWrap: "anywhere"
            },
            section: {
                marginTop: 9,
                paddingTop: 7,
                borderTop: "1px solid #292938"
            },
            sectionTitle: {
                color: "#a5b4fc",
                fontSize: 11,
                fontWeight: "bold",
                textTransform: "uppercase",
                letterSpacing: "0.5px",
                marginBottom: 6
            },
            description: {
                display: "flex",
                alignItems: "flex-start",
                gap: 7,
                marginTop: 6,
                lineHeight: 1.45
            },
            image: {
                width: 20,
                height: 20,
                objectFit: "contain",
                flexShrink: 0
            },
            text: {
                flex: 1,
                minWidth: 0,
                overflowWrap: "anywhere",
                whiteSpace: "pre-wrap",
                color: "#d1d5db"
            },
            badge: {
                display: "inline-block",
                background: "#242433",
                color: "#c4b5fd",
                borderRadius: 3,
                padding: "2px 5px",
                marginRight: 5,
                fontSize: 10
            },
            metadata: {
                display: "flex",
                flexWrap: "wrap",
                gap: 5,
                marginTop: 7
            },
            metadataItem: {
                background: "#191920",
                border: "1px solid #30303c",
                borderRadius: 4,
                padding: "4px 6px",
                color: "#cbd5e1",
                overflowWrap: "anywhere"
            },
            empty: {
                color: "#71717a",
                textAlign: "center",
                padding: 20
            }
        };
    }

    // Accepte un tableau directement ou un objet { sequences: [...] }.
    getSequences() {
        const sequences = this.props.sequences;

        if (Array.isArray(sequences)) {
            return sequences;
        }

        if (sequences && Array.isArray(sequences.sequences)) {
            return sequences.sequences;
        }

        return [];
    }

    getImage(source) {
        if (source === "IA") {
            return this.props.imgIA;
        }

        if (source === "HUMAN") {
            return this.props.imgHuman;
        }

        return null;
    }

    normalizeCode(code) {
        if (code === undefined || code === null) {
            return "";
        }

        return String(code)
            .trim()
            .replace(/^medtop:/i, "")
            .replace(/^0+/, "");
    }

    // Recherche un code IPTC dans les catégories et leurs enfants.
    getIptcInfo(code) {
        const codes = this.props.iptccodes || {};
        const normalized = this.normalizeCode(code);

        for (const categoryName of Object.keys(codes)) {
            const category = codes[categoryName];

            if (!category) {
                continue;
            }

            if (
                this.normalizeCode(category.iptc_code) === normalized
            ) {
                return {
                    category: categoryName,
                    topic: "",
                    definition: category.definition || ""
                };
            }

            const children = Array.isArray(category.children)
                ? category.children
                : [];

            for (const child of children) {
                if (
                    this.normalizeCode(child.iptc_code) === normalized
                ) {
                    return {
                        category: categoryName,
                        topic: child.topic || "",
                        definition: child.definition || ""
                    };
                }
            }
        }

        return {
            category: "",
            topic: "",
            definition: ""
        };
    }

    // Priorité à type, puis emotion.
    // emotion peut être un code texte ou un index numérique.
    getEmotionName(emotion) {
        const list = this.props.emotionsList || [];

        let value = emotion.type;

        if (value === undefined || value === null || value === "") {
            value = emotion.emotion;
        }

        if (value === undefined || value === null || value === "") {
            return "Inconnue";
        }

        let found = null;

        if (typeof value === "number" ||
            /^\d+$/.test(String(value))) {
            const numericValue = Number(value);

            // Privilégier un index numérique lorsqu'il est valide.
            if (numericValue >= 0 && numericValue < list.length) {
                found = list[numericValue];
            }
        }

        if (!found) {
            const normalized = String(value).toUpperCase();

            found = list.find(item =>
                String(item.emotion).toUpperCase() === normalized
            );
        }

        return found ? found.text : String(value);
    }

    formatTimeCode(value) {
        if (
            typeof this.props.formatTimeCodeFunction === "function" &&
            value !== undefined &&
            value !== null
        ) {
            return this.props.formatTimeCodeFunction(value);
        }

        return value === undefined || value === null
            ? "--:--"
            : String(value);
    }

    renderDescription(current, field, sourceField, label) {
        const description = current[field];

        if (
            description === undefined ||
            description === null ||
            String(description).trim() === ""
        ) {
            return null;
        }

        const image = this.getImage(current[sourceField]);

        return (
            <div style={this.styles.description} key={field}>
                {image && (
                    <img
                        src={image}
                        alt={current[sourceField] || ""}
                        title={current[sourceField] || ""}
                        style={this.styles.image}
                    />
                )}

                <div style={this.styles.text}>
                    <span style={this.styles.badge}>{label}</span>
                    {description}
                </div>
            </div>
        );
    }

    renderEmotions(current) {
        const emotions = Array.isArray(current.emotions)
            ? current.emotions
            : [];

        if (emotions.length === 0) {
            return null;
        }

        const thematictitle = this.props.language=="fr"?"Émotions":"Emotions";

        return (
            <div style={this.styles.section}>
                <div style={this.styles.sectionTitle}>{thematictitle}</div>

                {emotions.map((emotion, index) => {
                    const image = this.getImage(emotion.source);

                    return (
                        <div
                            key={`${emotion.type || emotion.emotion}-${index}`}
                            style={this.styles.description}
                        >
                            {image && (
                                <img
                                    src={image}
                                    alt={emotion.source || ""}
                                    title={emotion.source || ""}
                                    style={this.styles.image}
                                />
                            )}

                            <div style={this.styles.text}>
                                <div style={{ color: "#e9d5ff", fontWeight: 600 }}>
                                    {this.getEmotionName(emotion)}
                                </div>



                                <div style={this.styles.metadata}>
                                    {emotion.intensity !== undefined &&
                                        emotion.intensity !== null && (
                                            <span style={this.styles.metadataItem}>
                                                {this.libintensity} : {emotion.intensity}
                                            </span>
                                        )}

                                    {emotion.sentiment !== undefined &&
                                        emotion.sentiment !== null && (
                                            <span style={this.styles.metadataItem}>
                                                {this.libsentiment} : {emotion.sentiment}
                                            </span>
                                        )}

                                    {emotion.confidence !== undefined &&
                                        emotion.confidence !== null && (
                                            <span style={this.styles.metadataItem}>
                                                {this.libconfidence} :{" "}
                                                {Number.isFinite(Number(emotion.confidence))
                                                    ? `${(Number(emotion.confidence) * 100).toFixed(0)} %`
                                                    : emotion.confidence}
                                            </span>
                                        )}
                                </div>




                            </div>
                        </div>
                    );
                })}
            </div>
        );
    }

    renderThematics(current) {
        const thematictitle = this.props.language=="fr"?"Thématiques":"Themes";
        const thematics = Array.isArray(current.thematics)
            ? current.thematics
            : [];

        if (thematics.length === 0) {
            return null;
        }

        return (
            <div style={this.styles.section}>
                <div style={this.styles.sectionTitle}>{thematictitle}</div>

                {thematics.map((thematic, index) => {
                    const code = thematic.iptccode;
                    const info = this.getIptcInfo(code);
                    const image = this.getImage(thematic.source);

                    return (
                        <div
                            key={`${code || "topic"}-${index}`}
                            style={this.styles.description}
                        >
                            {image && (
                                <img
                                    src={image}
                                    alt={thematic.source || ""}
                                    title={thematic.source || ""}
                                    style={this.styles.image}
                                />
                            )}

                            <div style={this.styles.text}>
                                {info.category && (
                                    <div style={{ color: "#e9d5ff", fontWeight: 600 }}>
                                        {info.category}
                                    </div>
                                )}

                                {info.topic && (
                                    <div style={{ color: "#c4b5fd", marginTop: 2 }}>
                                        {info.topic}
                                    </div>
                                )}

                                {code && (
                                    <div style={{ marginTop: 3 }}>
                                        <span style={this.styles.badge}>
                                            IPTC {code}
                                        </span>

                                        {thematic.confidence !== undefined &&
                                            thematic.confidence !== null && (
                                                <span style={this.styles.badge}>
                                                    {this.libconfidence} :{" "}
                                                    {(Number(thematic.confidence) * 100).toFixed(0)} %
                                                </span>
                                            )}
                                    </div>
                                )}

                                {info.definition && (
                                    <div style={{ color: "#9ca3af", marginTop: 4 }}>
                                        {info.definition}
                                    </div>
                                )}

                                {!info.category && !info.topic && (
                                    <div style={{ color: "#9ca3af" }}>
                                        Code IPTC non référencé
                                    </div>
                                )}
                            </div>
                        </div>
                    );
                })}
            </div>
        );
    }

    renderMetadata(sequence) {
        const fields = [
            ["Lieu réel", sequence.real_place],
            ["Lieu fictif", sequence.fictional_place],
            ["Époque réelle", sequence.real_epoch],
            ["Époque fictive", sequence.fictional_epoch]
        ];

        const filledFields = fields.filter(
            ([, value]) =>
                value !== undefined &&
                value !== null &&
                String(value).trim() !== ""
        );

        if (filledFields.length === 0) {
            return null;
        }

        const titlelieu = this.props.language=="fr"?"Lieux et époques":"Places and periods";
        return (
            <div style={this.styles.section}>
                <div style={this.styles.sectionTitle}>{titlelieu}</div>

                <div style={this.styles.metadata}>
                    {filledFields.map(([label, value]) => (
                        <div key={label} style={this.styles.metadataItem}>
                            <span style={{ color: "#8b8ba3" }}>{label} : </span>
                            {value}
                        </div>
                    ))}
                </div>
            </div>
        );
    }

    renderSequence(sequence, index) {
        const current = sequence.current || {};

        const titlesequence = this.props.language=="fr"?"Séq.":"Seq.";

        return (
            <div key={`${sequence.in}-${index}`} style={this.styles.sequence}>
                <div style={this.styles.heading}>
                    <span style={this.styles.number}>
                        {titlesequence} {index + 1}
                    </span>

                    <span style={this.styles.timecode}>
                        {this.formatTimeCode(sequence.in)}
                    </span>

                    <span style={{ color: "#52525b" }}>→</span>

                    <span style={this.styles.timecode}>
                        {this.formatTimeCode(sequence.out)}
                    </span>
                </div>

                {sequence.title && (
                    <div style={this.styles.title}>
                        {sequence.title}
                    </div>
                )}

                {this.renderDescription(
                    current,
                    "descriptionA",
                    "sourceA",
                    this.props.language=="fr"?"Description courte":"Short description"
                )}

                {this.renderDescription(
                    current,
                    "descriptionB",
                    "sourceB",
                    this.props.language=="fr"?"Description longue":"Long description"
                )}

                {this.renderEmotions(current)}
                {this.renderThematics(current)}
                {this.renderMetadata(sequence)}
            </div>
        );
    }

    render() {
        const sequences = this.getSequences();

        return (
            <div style={this.styles.container} className="show-sequences">
                <style>{`
                    .show-sequences,
                    .show-sequences * {
                        box-sizing: border-box;
                    }

                    .show-sequences > div::-webkit-scrollbar {
                        display: none;
                        width: 0;
                        height: 0;
                    }
                `}</style>

                <div style={this.styles.list}>
                    {sequences.length === 0 ? (
                        <div style={this.styles.empty}>
                            Aucune séquence à afficher
                        </div>
                    ) : (
                        sequences.map((sequence, index) =>
                            this.renderSequence(sequence, index)
                        )
                    )}
                </div>
            </div>
        );
    }
}
